/**
 * 🌫️ PostTombHaze — global dream-haze overlay.
 *
 * Canon: TOMB_OF_AWAKENING_SCENE1_CANON_FEB2026.md § *Post-Tomb Residual*
 *
 * After the player leaves the Tomb of Awakening, the world feels
 * dream-hazed until they reach the Temporal Water Fountain in
 * Dragonious Castle. Location-gated, not time-gated.
 *
 * This component reads `/api/tomb/state` and renders a subtle
 * fixed-position overlay (desaturation + vignette + drifting motes)
 * while `residual_active === true`. Pointer-events-none.
 *
 * Rules:
 *   - Atmospheric only. No stat effects, no gameplay impact.
 *   - Suppressed on the /tomb route itself (scene has its own vibe).
 *   - Does NOT render for anonymous users or before load.
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PostTombHaze() {
  const [intensity, setIntensity] = useState("clear"); // "full" | "partial" | "clear"
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;
    const fetchState = async () => {
      try {
        const { data } = await axios.get(`${API}/tomb/state`, { withCredentials: true });
        if (!cancelled) setIntensity(data?.residual_intensity || "clear");
      } catch {
        if (!cancelled) setIntensity("clear");
      }
    };
    fetchState();
    // Re-check every 60s in case Cassandra/Ledger intervene, or the
    // player hits the Temporal Water Fountain in another view.
    const id = setInterval(fetchState, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [location.pathname]); // re-check on route change

  // Suppress on /tomb — the scene has its own atmosphere.
  const onTombRoute = location.pathname.startsWith("/tomb");
  if (intensity === "clear" || onTombRoute) return null;

  // Canon Feb 14 2026: partial (Cassandra/Ledger intervened) is
  // roughly half-strength of full. Motes freeze on partial.
  const isPartial = intensity === "partial";

  return (
    <div
      data-testid="post-tomb-haze"
      data-intensity={intensity}
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-[60]"
      style={{
        background: [
          `radial-gradient(ellipse at 50% 50%, rgba(0,0,0,0) 55%, rgba(20,30,50,${isPartial ? 0.18 : 0.32}) 100%)`,
          `linear-gradient(180deg, rgba(96,180,255,${isPartial ? 0.015 : 0.03}) 0%, rgba(20,10,30,${isPartial ? 0.025 : 0.05}) 100%)`,
        ].join(", "),
        mixBlendMode: "multiply",
        animation: isPartial ? undefined : "postTombDrift 18s ease-in-out infinite alternate",
      }}
    >
      <div
        className="absolute inset-0"
        style={{
          background: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9' numOctaves='2' seed='3'/%3E%3CfeColorMatrix values='0 0 0 0 0.7 0 0 0 0 0.85 0 0 0 0 1 0 0 0 0.09 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
          opacity: isPartial ? 0.28 : 0.55,
        }}
      />
      <style>{`
        @keyframes postTombDrift {
          0%   { filter: hue-rotate(0deg)  brightness(0.98); }
          100% { filter: hue-rotate(-8deg) brightness(1.02); }
        }
      `}</style>
    </div>
  );
}
